import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CustomFieldInputs,
  ItemReservationHint,
  ItemMaintenancePanel,
  ItemMovementPanel,
  ItemOperationalTimeline,
  ItemUpcomingReservations,
} from '@/modules/inventory/components';
import { useItemReservationOverlays } from '@/modules/reservations/hooks';
import {
  getItem,
  listCustomFieldsForItem,
  saveItem,
} from '@/modules/inventory/services';
import { useCategories, useSubcategories } from '@/modules/inventory/hooks';
import {
  INVENTORY_MODE_LABELS,
  INVENTORY_STATUS_LABELS,
  type CustomFieldDefinition,
  type InventoryItem,
  type InventoryItemInput,
  type InventoryMode,
  type InventoryStatus,
} from '@/modules/inventory/types';
import {
  emptyValueForFieldType,
  validateCustomFieldValues,
} from '@/modules/inventory/utils';
import { modulePaths } from '@/routes/paths';
import { EntityAuditTrail } from '@/shared/components/EntityAuditTrail';
import { useAuth } from '@/shared/hooks/useAuth';

const defaultInput: InventoryItemInput = {
  sku: '',
  name: '',
  description: '',
  categoryId: '',
  subcategoryId: null,
  mode: 'sale',
  status: 'available',
  customFieldValues: {},
  trackStock: false,
  stockQuantity: 0,
  stockUnit: 'un',
};

export function ItemFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'nuevo';
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('inventory.write');
  const canViewCalendar = hasPermission('calendar.read');
  const { overlays } = useItemReservationOverlays();

  const { categories } = useCategories();
  const { subcategories } = useSubcategories();

  const [form, setForm] = useState<InventoryItemInput>(defaultInput);
  const [loadedItem, setLoadedItem] = useState<InventoryItem | null>(null);
  const [fieldDefs, setFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredSubs = subcategories.filter(
    (s) => s.categoryId === form.categoryId && s.active,
  );

  useEffect(() => {
    if (!form.categoryId) {
      setFieldDefs([]);
      return;
    }
    void listCustomFieldsForItem(form.categoryId, form.subcategoryId).then(
      setFieldDefs,
    );
  }, [form.categoryId, form.subcategoryId]);

  useEffect(() => {
    if (isNew || !id) return;
    setLoading(true);
    void getItem(id)
      .then((item) => {
        if (!item) {
          setError('Artículo no encontrado.');
          return;
        }
        setLoadedItem(item);
        setForm({
          sku: item.sku,
          name: item.name,
          description: item.description,
          categoryId: item.categoryId,
          subcategoryId: item.subcategoryId,
          mode: item.mode,
          status: item.status,
          customFieldValues: item.customFieldValues,
          trackStock: item.trackStock,
          stockQuantity: item.stockQuantity,
          stockUnit: item.stockUnit,
        });
      })
      .catch(() => setError('Error al cargar el artículo.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    if (isNew && fieldDefs.length > 0) {
      setForm((prev) => {
        const next = { ...prev.customFieldValues };
        for (const f of fieldDefs) {
          if (next[f.key] === undefined) {
            next[f.key] = emptyValueForFieldType(f.type);
          }
        }
        return { ...prev, customFieldValues: next };
      });
    }
  }, [fieldDefs, isNew]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite) return;
    if (!form.sku.trim() || !form.name.trim() || !form.categoryId) {
      setError('SKU, nombre y categoría son obligatorios.');
      return;
    }
    const fieldError = validateCustomFieldValues(fieldDefs, form.customFieldValues);
    if (fieldError) {
      setError(fieldError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveItem(form, isNew ? undefined : id);
      void navigate(`${modulePaths.inventory}/${saved.id}`, {
        replace: true,
      });
    } catch {
      setError('No se pudo guardar el artículo.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;

  return (
    <section>
      <p>
        <Link to={modulePaths.inventory}>← Volver al listado</Link>
      </p>
      <h1>{isNew ? 'Nuevo artículo' : 'Editar artículo'}</h1>

      {!isNew && id && form.mode === 'rental' && overlays.has(id) ? (
        <div className="inventory-panel inventory-panel--hint">
          <ItemReservationHint overlay={overlays.get(id)!} />
          <p className="auth-muted">
            Disponibilidad temporal por reservas (hoy). El estado operativo del
            artículo se gestiona aparte.
          </p>
        </div>
      ) : null}

      {!isNew && id && form.mode === 'rental' && canViewCalendar ? (
        <ItemUpcomingReservations itemId={id} />
      ) : null}

      {!isNew && loadedItem ? (
        <>
          <ItemMaintenancePanel item={loadedItem} />
          <ItemMovementPanel
            item={loadedItem}
            onUpdated={() => {
              if (!id) return;
              void getItem(id).then((item) => item && setLoadedItem(item));
            }}
          />
          <div className="inventory-panel">
            <h2>Trazabilidad operativa</h2>
            <ItemOperationalTimeline itemId={loadedItem.id} />
          </div>
        </>
      ) : null}

      <form className="inventory-panel" onSubmit={handleSubmit}>
        <div className="users-form-grid">
          <label className="auth-label">
            SKU *
            <input
              required
              disabled={!canWrite}
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Nombre *
            <input
              required
              disabled={!canWrite}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Categoría *
            <select
              required
              disabled={!canWrite}
              value={form.categoryId}
              onChange={(e) =>
                setForm({
                  ...form,
                  categoryId: e.target.value,
                  subcategoryId: null,
                })
              }
            >
              <option value="">—</option>
              {categories
                .filter((c) => c.active)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="auth-label">
            Subcategoría
            <select
              disabled={!canWrite || !form.categoryId}
              value={form.subcategoryId ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  subcategoryId: e.target.value || null,
                })
              }
            >
              <option value="">— Ninguna —</option>
              {filteredSubs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="auth-label">
            Modo
            <select
              disabled={!canWrite}
              value={form.mode}
              onChange={(e) =>
                setForm({ ...form, mode: e.target.value as InventoryMode })
              }
            >
              {(Object.keys(INVENTORY_MODE_LABELS) as InventoryMode[]).map(
                (m) => (
                  <option key={m} value={m}>
                    {INVENTORY_MODE_LABELS[m]}
                  </option>
                ),
              )}
            </select>
          </label>
          <label className="auth-label">
            Estado
            <select
              disabled={!canWrite}
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value as InventoryStatus })
              }
            >
              {(Object.keys(INVENTORY_STATUS_LABELS) as InventoryStatus[]).map(
                (s) => (
                  <option key={s} value={s}>
                    {INVENTORY_STATUS_LABELS[s]}
                  </option>
                ),
              )}
            </select>
          </label>
        </div>

        <label className="auth-label">
          Descripción
          <textarea
            rows={2}
            disabled={!canWrite}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>

        <fieldset className="inventory-fieldset">
          <legend>Stock</legend>
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              disabled={!canWrite}
              checked={form.trackStock}
              onChange={(e) => setForm({ ...form, trackStock: e.target.checked })}
            />
            Controlar stock
          </label>
          {form.trackStock ? (
            <div className="users-form-grid">
              <label className="auth-label">
                Cantidad
                <input
                  type="number"
                  min={0}
                  disabled={!canWrite}
                  value={form.stockQuantity}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      stockQuantity: Number(e.target.value) || 0,
                    })
                  }
                />
              </label>
              <label className="auth-label">
                Unidad
                <input
                  disabled={!canWrite}
                  value={form.stockUnit}
                  onChange={(e) => setForm({ ...form, stockUnit: e.target.value })}
                />
              </label>
            </div>
          ) : null}
        </fieldset>

        <fieldset className="inventory-fieldset">
          <legend>Campos personalizados</legend>
          <CustomFieldInputs
            definitions={fieldDefs}
            values={form.customFieldValues}
            disabled={!canWrite}
            onChange={(key, value) =>
              setForm({
                ...form,
                customFieldValues: { ...form.customFieldValues, [key]: value },
              })
            }
          />
        </fieldset>

        {error ? <p className="auth-error">{error}</p> : null}

        {canWrite ? (
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        ) : null}
      </form>

      {!isNew && id ? (
        <EntityAuditTrail entityType="inventoryItem" entityId={id} />
      ) : null}
    </section>
  );
}
